import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { PlusIcon, TeachingIcon } from '@repo/ui/icons'
import { useRef } from 'react'

import { API, constructApiUrl } from '../../../api/apis.config'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import TeflAverageRating from '../../../components/pages/ols/ol-tefl/tefl-average-rating'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { Tefl } from '../../../lib/types/models/tefl/tefl'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'

const TeflListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<Tefl>>(null)

  const { user } = useAuth()
  const olId = user?.ol?.id

  const columns: Column<Tefl>[] = [
    {
      enableSorting: true,
      header: 'Last name',
      accessorKey: 'lastName'
    },
    {
      enableSorting: true,
      header: 'First Name',
      accessorKey: 'firstName'
    },
    {
      header: 'Phone number',
      accessorKey: 'phoneNumber',
      cell: ({ row }) => formatPhoneNumber(row.original.phoneNumber)
    },
    {
      accessorKey: 'seniorityStartedAt',
      header: 'Seniority (years)',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.seniorityStartedAt
          ? DateFormatter.getYearsDifference(new Date(), new Date(row.original.seniorityStartedAt))
          : '-'
    },
    {
      accessorKey: 'centerTeflsCount',
      header: 'Nº of stays',
      enableSorting: true
    },
    {
      // Only the ratings LEC diffused to the provider (backend rule)
      accessorKey: 'averageRating',
      header: 'Course average grade',
      enableSorting: true,
      cell: ({ row }) => <TeflAverageRating {...row.original} />
    }
  ]

  if (!olId) return <LoadingScreen />

  return (
    <div>
      <UserNavBar title="TEFL" icon={TeachingIcon} />
      <div className={'h-4'} />
      <DynamicDataTable<Tefl>
        ref={tableRef}
        title={'All TEFL'}
        deleteMessage={{
          name: 'TEFL',
          attributeName: 'lastName',
          deleteAll: 'Are you sure you want to delete all TEFLs ?',
          deleteOne: (tefl) =>
            `Are you sure you want to delete "${tefl.lastName.toUpperCase()} ${StringFormatter.capitalize(tefl.firstName)}" ?`,
          deleteMany: (data) => {
            const names = data
              .map(
                (tefl) =>
                  `${tefl.lastName.toUpperCase()} ${StringFormatter.capitalize(tefl.firstName)}`
              )
              .join('", "')
            return `Are you sure you want to delete "${names}" ?`
          }
        }}
        columns={columns}
        selectable
        allowSearchParam
        onRowClick={(tefl, event) =>
          navigateWithEvent(
            Paths.OL_TEFL_UPDATE_PAGE.replace(':id', tefl.id.toString()).replace(
              ':olId',
              tefl.olId.toString()
            ),
            event
          )
        }
        endpoint={constructApiUrl(API.tefl.getAllTeflByOlId.url, { olId })}
        version={'FULL'}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Add a new TEFL',
            fn: () => navigate(Paths.OL_TEFL_CREATE_PAGE)
          }
        ]}
      />
    </div>
  )
}

export default TeflListPage
